import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import {
  CardContent,
  CardHeader,
  CardTitle,
  TruncatedText,
  type CardProps,
} from "@/components/ui";
import { cn } from "@/lib/utils";
import { ModifiedSectionCard } from "./ModifiedSectionCard";

export interface SectionCardProps extends Omit<CardProps, "title"> {
  title: string;
  icon?: LucideIcon;
  /** Shown below the title row (hint text). */
  description?: ReactNode;
  /** Header action (e.g. add button), aligned opposite the title. */
  action?: ReactNode;
  /** Extra badge beside the title (e.g. auto-save chip). */
  badge?: ReactNode;
  /** Optional count pill after the title. */
  count?: number;
  modified?: boolean;
  contentClassName?: string;
  /** Icon presentation: boxed well (default) or plain inline icon. */
  iconTone?: "well" | "plain";
  iconClassName?: string;
  titleClassName?: string;
  countClassName?: string;
  /** Rendered below body, inside the card shell (e.g. profile save/revert bar). */
  footer?: ReactNode;
  children: ReactNode;
}

export function SectionCard({
  title,
  icon: Icon,
  description,
  action,
  badge,
  count,
  modified = false,
  className,
  contentClassName,
  iconTone = "well",
  iconClassName,
  titleClassName,
  countClassName,
  footer,
  children,
  ...cardProps
}: SectionCardProps) {
  const header = (
    <CardHeader
      className={cn(
        "px-5 py-3.5",
        modified && "pr-24 sm:pr-28",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <CardTitle
          className={cn(
            "flex min-w-0 flex-1 items-center gap-2.5 text-[15px] font-semibold tracking-tight text-surface-900",
            titleClassName,
          )}
        >
          {Icon ? (
            iconTone === "well" ? (
              <span
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-surface-200 bg-surface-50 text-surface-600"
              >
                <Icon className={cn("h-4 w-4", iconClassName)} aria-hidden />
              </span>
            ) : (
              <Icon className={cn("h-4 w-4 shrink-0 text-surface-500", iconClassName)} aria-hidden />
            )
          ) : null}
          <TruncatedText className="min-w-0">{title}</TruncatedText>
          {count != null && count > 0 ? (
            <span
              className={cn(
                "rounded-md bg-surface-100 px-1.5 py-0.5 text-xs font-medium tabular-nums text-surface-600",
                countClassName,
              )}
            >
              {count}
            </span>
          ) : null}
          {badge}
        </CardTitle>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      {description ? (
        <div className="mt-1 text-[13px] text-surface-500">{description}</div>
      ) : null}
    </CardHeader>
  );

  const body = (
    <CardContent
      className={cn(
        "min-w-0 flex-1 space-y-3.5 px-5 pb-5 pt-4",
        contentClassName,
      )}
    >
      {children}
    </CardContent>
  );

  const inner = (
    <>
      {header}
      {body}
    </>
  );

  return (
    <ModifiedSectionCard
      modified={modified}
      className={cn("flex min-w-0 flex-col", className)}
      {...cardProps}
    >
      {inner}
      {footer}
    </ModifiedSectionCard>
  );
}
